import { FC, useEffect, useRef } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import { useI18n } from "../../../hooks/useTranslations";
import { useCostTypeCategoriesApi } from "../../../http/costTypeCategories/api";
import { CostTypeCategory } from "../../../http/costTypeCategories/types";
import { CostType } from "../../../http/costTypes/types";
import Form from "../../ui/Form";
import FormField from "../../ui/FormField";
import FormFieldsContainer from "../../ui/FormFieldsContainer";
import Input from "../../ui/Input";
import MultipleInput from "../../ui/MultipleInput";
import reactSelectStyles from "../../ui/ReactSelectUtils";

const COST_TYPE_CATEGORY_FIELDS = ["id", "name"] as const;
type CostTypeCategoryField = (typeof COST_TYPE_CATEGORY_FIELDS)[number];
type CostTypeCategorySearch = Pick<CostTypeCategory, CostTypeCategoryField>;

export interface CostTypeFormBody {
  id: string;
  category: CostTypeCategorySearch;
  name: string;
  dkvProductCodes: string[];
  pkwAccountingNumber: string;
  lkwAccountingNumber: string;
  pkwSuperiorAccountingNumber: string;
  costObject: string;
}

type CostTypeFormProps = {
  costType?: CostType | null;
  onSubmit: (form: CostTypeFormBody) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
};

const isDkvProductCodesValid = (values?: string[]) => !!values?.length && values.every((value) => value.trim() !== "");

const CostTypeForm: FC<CostTypeFormProps> = ({ costType, onSubmit, isDisabled, isLoading }: CostTypeFormProps) => {
  const i18n = useI18n();
  const categoryCostTypeApi = useCostTypeCategoriesApi();
  const aborter = useRef(new AbortController());

  const categories = categoryCostTypeApi.useAdminCostTypeCategories(
    { limit: 100, fields: COST_TYPE_CATEGORY_FIELDS },
    aborter.current.signal,
  );

  const defaultValues = {
    category: costType?.category,
    name: costType?.name,
    dkvProductCodes: costType?.dkvProductCodes ?? [],
    pkwAccountingNumber: costType?.pkwAccountingNumber,
    lkwAccountingNumber: costType?.lkwAccountingNumber,
    pkwSuperiorAccountingNumber: costType?.pkwSuperiorAccountingNumber,
    costObject: costType?.costObject,
  };
  const dkvProductCodesDefaultValues = defaultValues.dkvProductCodes.length ? defaultValues.dkvProductCodes : [""];
  const dkvProductCodesInputKey = costType
    ? `${costType.id}-${defaultValues.dkvProductCodes.join("-")}`
    : "new-cost-type";

  return (
    <Form<CostTypeFormBody>
      defaultValues={defaultValues}
      disabled={isDisabled}
      onSubmit={() => onSubmit}
      submitText={i18n.t.common.save}
      itemCreationInfo={costType}
    >
      {({ register, formState: { errors }, control, reset, setValue }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [costType]);

        return (
          <FormFieldsContainer>
            <FormField label={i18n.t.common.name} error={errors.name}>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading}
                  {...register("name", {
                    required: true,
                  })}
                />
              )}
            </FormField>
            <Controller
              name="category"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.category} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Select
                      menuPosition="fixed"
                      {...field}
                      inputId={labelId}
                      isDisabled={isDisabled || isLoading}
                      isLoading={isLoading}
                      options={categories.data?.items}
                      placeholder={`${i18n.t.common.select}...`}
                      getOptionLabel={(x) => x.name}
                      getOptionValue={(x) => x.id}
                      styles={reactSelectStyles<CostTypeCategorySearch>(isInvalid)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="dkvProductCodes"
              control={control}
              rules={{
                required: true,
                validate: (values) => isDkvProductCodesValid(values),
              }}
              render={({ field, fieldState }) => {
                const isInvalid =
                  !isDisabled && !isLoading && fieldState.invalid
                    ? (field.value ?? []).map((value) => value.trim() === "")
                    : undefined;

                return (
                  <FormField label={i18n.t.common.dkvProductCodes} error={fieldState.error}>
                    <MultipleInput
                      key={dkvProductCodesInputKey}
                      defaultValues={field.value?.length ? field.value : dkvProductCodesDefaultValues}
                      onChange={(values) => {
                        field.onChange(values);
                        setValue("dkvProductCodes", values, { shouldDirty: true, shouldValidate: true });
                      }}
                      disabled={isDisabled || isLoading}
                      isInvalid={isInvalid}
                    />
                  </FormField>
                );
              }}
            />
            <FormField label={i18n.t.common.pkwAccountingNumber} error={errors.pkwAccountingNumber} isOptional>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading}
                  {...register("pkwAccountingNumber")}
                />
              )}
            </FormField>
            <FormField label={i18n.t.common.lkwAccountingNumber} error={errors.lkwAccountingNumber} isOptional>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading}
                  {...register("lkwAccountingNumber")}
                />
              )}
            </FormField>
            <FormField
              label={i18n.t.common.pkwSuperiorAccountingNumber}
              error={errors.pkwSuperiorAccountingNumber}
              isOptional
            >
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading}
                  {...register("pkwSuperiorAccountingNumber")}
                />
              )}
            </FormField>
            <FormField label={i18n.t.common.costObject} error={errors.costObject} isOptional>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading}
                  {...register("costObject")}
                />
              )}
            </FormField>
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default CostTypeForm;
